import { useEffect, useState } from 'react';
import MojitoPresentation from 'mojito/presentation';
import InPlayTypes from 'modules/inplay/types.js';
import InPlayUtils from 'modules/inplay/utils.js';

const { FlexPane, LoaderSuspense, MenuRow, MenuRowSkeleton } = MojitoPresentation.Components;
const { IntentActionTrigger } = MojitoPresentation.Base.Intent;
const { ALL_SPORTS_CODE, STREAMING, FAVORITES } = InPlayTypes.TABS;
const resolveActualSportId = sportId => (sportId === ALL_SPORTS_CODE ? undefined : sportId);

const getSelectedTab = (selectedTab, config) =>
    selectedTab || (config.autoSelectAllItem ? ALL_SPORTS_CODE : undefined);

const getItemDescriptors = (config, stringResolver, sportIds) => {
    const itemsMap = {
        all: {
            sportId: ALL_SPORTS_CODE,
            label: stringResolver.resolveString('$INPLAY.INPLAY_MENU_ALL'),
        },
        favorites: {
            sportId: FAVORITES,
            label: stringResolver.resolveString('$INPLAY.INPLAY_MENU_FAVORITES'),
        },
        streaming: {
            sportId: STREAMING,
            label: stringResolver.resolveString('$INPLAY.INPLAY_MENU_STREAMING'),
        },
        sports: sportIds
            .filter(id => id !== STREAMING && id !== FAVORITES)
            .map(sportId => ({ sportId })),
    };

    return config.menuItems.flatMap(item => itemsMap[item]);
};

/**
 * Filters menu items to only include those with more than zero events.
 *
 * @param {Array} menuItems - Array of menu item descriptors.
 * @param {object} eventGroups - Event groups data.
 * @param {Array} favoriteEvents - Favorite events data.
 * @param {object} appContext - Application context for sport names.
 * @param {object} config - Configuration object.
 * @returns {Array} Filtered menu items with event counts and labels.
 */
function filterMenuItemsWithEvents(menuItems, eventGroups, favoriteEvents, appContext, config) {
    return menuItems
        .map(item => {
            const { sportId, label } = item;
            const eventCount = InPlayUtils.countEventsForInplayMenuTab(
                sportId,
                eventGroups,
                favoriteEvents
            );
            const sportName = appContext.sportName(sportId);
            // Always show favorites, even if 0 events
            if (sportId === FAVORITES) {
                return {
                    id: sportId,
                    data: `${label || sportName} (${eventCount})`,
                    icon: config.sportIcons[sportId] || config.sportIcons.defaultIcon || undefined,
                    altText: sportName,
                };
            }
            if (eventCount === 0) {
                return null;
            }
            return {
                id: sportId,
                data: `${label || sportName} (${eventCount})`,
                icon: config.sportIcons[sportId] || config.sportIcons.defaultIcon || undefined,
                altText: sportName,
            };
        })
        .filter(Boolean);
}

export default function InPlayMenu(props) {
    const {
        selectedTab,
        hasPendingGroups,
        cbSportClicked,
        eventGroups,
        sportIds,
        favoriteEvents,
        mojitoTools,
    } = props;
    const { config, stringResolver, emitPerformanceMark, appContext } = mojitoTools;
    const [isContentShown, setIsContentShown] = useState(false);

    const onMenuItemClicked = sportId => {
        cbSportClicked && cbSportClicked(sportId);
        config.redirect && IntentActionTrigger.showInPlay(resolveActualSportId(sportId));
    };

    const skeleton = <MenuRowSkeleton config={config.navigationMenuSkeleton} />;

    useEffect(() => {
        emitPerformanceMark('moduleRendered', !hasPendingGroups);
    }, [emitPerformanceMark, hasPendingGroups]);

    const getMenuItems = () => {
        // Only assembles menu item descriptors, no filtering
        return getItemDescriptors(config, stringResolver, sportIds);
    };

    const mapMenuItemsForMenuRow = menuItems => {
        return menuItems.map(item => {
            const { sportId, label } = item;
            const sportName = appContext.sportName(sportId);
            return {
                id: sportId,
                data: label || sportName,
                icon: config.sportIcons[sportId] || config.sportIcons.defaultIcon || undefined,
                altText: sportName,
            };
        });
    };

    const renderNavigation = selectedTab => {
        const selectedMenuItem = getSelectedTab(selectedTab, config);
        if (hasPendingGroups) {
            const menuItems = mapMenuItemsForMenuRow(getMenuItems());
            return (
                <LoaderSuspense
                    config={config.skeletonLoader}
                    loader={skeleton}
                    isContentPending={hasPendingGroups}
                    onShowContent={() => setIsContentShown(true)}
                >
                    {isContentShown && (
                        <FlexPane config={config.navigationMenuContainer}>
                            <MenuRow
                                config={config.navigationMenu}
                                menuItems={menuItems}
                                selectedItemId={selectedMenuItem}
                                cbItemClicked={onMenuItemClicked}
                            />
                        </FlexPane>
                    )}
                </LoaderSuspense>
            );
        }

        // After loading, filter menu items with >0 events
        const menuItems = filterMenuItemsWithEvents(
            getMenuItems(),
            eventGroups,
            favoriteEvents,
            appContext,
            config
        );
        if (!menuItems.length) {
            return null;
        }
        return (
            <LoaderSuspense
                config={config.skeletonLoader}
                loader={skeleton}
                isContentPending={hasPendingGroups}
                onShowContent={() => setIsContentShown(true)}
            >
                {isContentShown && (
                    <FlexPane config={config.navigationMenuContainer}>
                        <MenuRow
                            config={config.navigationMenu}
                            menuItems={menuItems}
                            selectedItemId={selectedMenuItem}
                            cbItemClicked={onMenuItemClicked}
                        />
                    </FlexPane>
                )}
            </LoaderSuspense>
        );
    };

    return (
        <FlexPane config={config.rootContainer} class="ta-InPlayMenu">
            {renderNavigation(selectedTab)}
        </FlexPane>
    );
}
